import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, CircularProgress, RadioGroup, FormControlLabel, Radio,
    TextField, FormGroup, Checkbox, Select, MenuItem, FormControl, InputLabel,
    Button, Divider, Avatar, Card, CardContent
} from '@mui/material';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';
import MembershipPaymentDialog from './MembershipPaymentDialog';

const COLORS = {
    primary: '#2D4A3D',
    text: '#1a1a2e',
    textSecondary: '#6b7280',
    border: '#e5e7eb',
    background: '#f9fafb',
    white: '#ffffff',
};

const UGX_TO_USD = 1 / 3300;

const billingLabel = (cycle) => {
    if (cycle === 'monthly') return '/month';
    if (cycle === 'yearly') return '/year';
    return ' one-time';
};

// Prices are stored as UGX integers in the DB
const formatUGX = (ugx) => `UGX ${Math.round(ugx).toLocaleString()}`;

const MembershipFormInner = () => {
    const { slug } = useParams();

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [applicantName, setApplicantName] = useState('');
    const [applicantEmail, setApplicantEmail] = useState('');
    const [voluntaryAmount, setVoluntaryAmount] = useState('');
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

    useEffect(() => {
        const fetchForm = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${baseURL}/membership/form/${slug}`);
                if (!res.ok) {
                    const data = await res.json();
                    throw new Error(data.error || 'Form not found.');
                }
                const data = await res.json();
                setFormData(data.form);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchForm();
    }, [slug]);

    const handleFieldChange = (fieldKey, value) => {
        setFieldValues(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleCheckboxChange = (fieldKey, option, checked) => {
        setFieldValues(prev => {
            const existing = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
            if (checked) return { ...prev, [fieldKey]: [...existing, option] };
            return { ...prev, [fieldKey]: existing.filter(v => v !== option) };
        });
    };

    // Voluntary amount entered in UGX; category price already in UGX
    const voluntaryUGX = parseFloat(voluntaryAmount.replace(/,/g, '')) || 0;
    const baseUGX = parseFloat(selectedCategory?.price) || 0;
    const totalUGX = baseUGX + voluntaryUGX;

    const formType = formData?.form_type || 'membership';
    const isEvent = formType === 'event';

    const validate = () => {
        if (!selectedCategory) {
            toast.error(isEvent ? 'Please select a ticket type.' : 'Please select a membership tier.');
            return false;
        }
        if (!applicantName.trim()) { toast.error('Please enter your name.'); return false; }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(applicantEmail)) { toast.error('Please enter a valid email address.'); return false; }

        for (const field of (formData?.fields || [])) {
            if (field.is_required) {
                const val = fieldValues[field.field_key];
                const isEmpty = val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
                if (isEmpty) {
                    toast.error(`"${field.field_label}" is required.`);
                    return false;
                }
            }
        }
        return true;
    };

    const handleProceed = () => {
        if (!validate()) return;
        setPaymentDialogOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: COLORS.primary }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 600 }}>{error}</Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    The membership form you're looking for may no longer be available.
                </Typography>
            </Box>
        );
    }

    const basePriceDisplay = selectedCategory ? formatUGX(selectedCategory.price) : 'UGX 0';

    return (
        <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', py: 5 }}>
            <Box sx={{ maxWidth: 680, mx: 'auto', px: 2 }}>

                {/* Org Header */}
                <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                        <Avatar
                            src={formData.organization.logo_url || undefined}
                            alt={formData.organization.name}
                            sx={{ width: 56, height: 56, bgcolor: COLORS.primary, fontSize: '1.4rem' }}
                        >
                            {formData.organization.name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.text, fontSize: '1.25rem', lineHeight: 1.2 }}>
                                {formData.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.25 }}>
                                {formData.organization.name}
                            </Typography>
                        </Box>
                    </CardContent>
                    {formData.description && (
                        <Box sx={{ px: 3, pb: 3 }}>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.7 }}>
                                {formData.description}
                            </Typography>
                        </Box>
                    )}
                </Card>

                {/* Category Selector */}
                <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.text, mb: 2 }}>
                            {isEvent ? 'Choose your ticket type' : 'Choose your membership tier'}
                        </Typography>
                        <RadioGroup
                            value={selectedCategory?.id?.toString() || ''}
                            onChange={(e) => {
                                const cat = formData.categories.find(c => c.id.toString() === e.target.value);
                                setSelectedCategory(cat);
                            }}
                        >
                            {formData.categories.map((cat) => (
                                <Box
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', p: 2, mb: 1.5,
                                        borderRadius: '12px', cursor: 'pointer',
                                        border: `2px solid ${selectedCategory?.id === cat.id ? COLORS.primary : COLORS.border}`,
                                        backgroundColor: selectedCategory?.id === cat.id ? `${COLORS.primary}08` : COLORS.white,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <Radio
                                        value={cat.id.toString()}
                                        sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary }, mr: 1, p: 0.5 }}
                                        checked={selectedCategory?.id === cat.id}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontWeight: 700, color: COLORS.text, fontSize: '0.95rem' }}>
                                            {cat.name}
                                        </Typography>
                                        {cat.description && (
                                            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                                                {cat.description}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontWeight: 800, color: COLORS.primary, fontSize: '1.05rem' }}>
                                            {formatUGX(cat.price)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                                            {billingLabel(cat.billing_cycle)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </RadioGroup>
                    </CardContent>
                </Card>

                {/* Your Information */}
                <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.text, mb: 2 }}>
                            Your information
                        </Typography>
                        <TextField
                            fullWidth label="Full Name" value={applicantName}
                            onChange={e => setApplicantName(e.target.value)}
                            size="small" margin="dense" required
                            sx={{ mb: 1 }}
                        />
                        <TextField
                            fullWidth label="Email Address" value={applicantEmail} type="email"
                            onChange={e => setApplicantEmail(e.target.value)}
                            size="small" margin="dense" required
                        />

                        {/* Custom Fields */}
                        {formData.fields?.length > 0 && (
                            <>
                                <Divider sx={{ my: 2.5 }} />
                                {formData.fields.map((field) => (
                                    <Box key={field.id} sx={{ mb: 2 }}>
                                        {field.field_type === 'text' && (
                                            <TextField
                                                fullWidth
                                                label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                value={fieldValues[field.field_key] || ''}
                                                onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                size="small"
                                            />
                                        )}
                                        {field.field_type === 'email' && (
                                            <TextField
                                                fullWidth type="email"
                                                label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                value={fieldValues[field.field_key] || ''}
                                                onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                size="small"
                                            />
                                        )}
                                        {field.field_type === 'phone' && (
                                            <TextField
                                                fullWidth type="tel"
                                                label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                value={fieldValues[field.field_key] || ''}
                                                onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                size="small"
                                            />
                                        )}
                                        {field.field_type === 'textarea' && (
                                            <TextField
                                                fullWidth multiline rows={3}
                                                label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                value={fieldValues[field.field_key] || ''}
                                                onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                size="small"
                                            />
                                        )}
                                        {field.field_type === 'date' && (
                                            <TextField
                                                fullWidth type="date"
                                                label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                value={fieldValues[field.field_key] || ''}
                                                onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                        {field.field_type === 'select' && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{`${field.field_label}${field.is_required ? ' *' : ''}`}</InputLabel>
                                                <Select
                                                    value={fieldValues[field.field_key] || ''}
                                                    label={`${field.field_label}${field.is_required ? ' *' : ''}`}
                                                    onChange={e => handleFieldChange(field.field_key, e.target.value)}
                                                >
                                                    {(Array.isArray(field.options) ? field.options : []).map(opt => (
                                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {field.field_type === 'checkbox' && (
                                            <Box>
                                                <Typography variant="body2" sx={{ color: COLORS.text, fontWeight: 500, mb: 0.5, fontSize: '0.875rem' }}>
                                                    {field.field_label}{field.is_required ? ' *' : ''}
                                                </Typography>
                                                <FormGroup>
                                                    {(Array.isArray(field.options) ? field.options : []).map(opt => (
                                                        <FormControlLabel
                                                            key={opt}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={(fieldValues[field.field_key] || []).includes(opt)}
                                                                    onChange={e => handleCheckboxChange(field.field_key, opt, e.target.checked)}
                                                                    sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }}
                                                                />
                                                            }
                                                            label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{opt}</Typography>}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Voluntary Contribution */}
                <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.text, mb: 0.5 }}>
                            Additional contribution (optional)
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                            Support {formData.organization.name} with an extra voluntary donation on top of your {isEvent ? 'registration fee' : 'membership fee'}.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Extra amount (UGX)"
                            value={voluntaryAmount}
                            onChange={e => {
                                const digits = e.target.value.replace(/[^0-9]/g, '');
                                setVoluntaryAmount(digits ? Number(digits).toLocaleString('en-UG') : '');
                            }}
                            size="small"
                            placeholder="0"
                            inputProps={{ inputMode: 'numeric' }}
                        />
                    </CardContent>
                </Card>

                {/* Total & CTA */}
                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: `1px solid ${COLORS.border}` }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
                                {selectedCategory
                                    ? `${selectedCategory.name} ${isEvent ? 'ticket' : 'membership'}`
                                    : (isEvent ? 'Select a ticket type above' : 'Select a tier above')}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: COLORS.text }}>
                                {basePriceDisplay}
                            </Typography>
                        </Box>
                        {voluntaryUGX > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>Additional contribution</Typography>
                                <Typography sx={{ fontWeight: 600, color: COLORS.text }}>
                                    +{formatUGX(voluntaryUGX)}
                                </Typography>
                            </Box>
                        )}
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography sx={{ fontWeight: 700, color: COLORS.text, fontSize: '1rem' }}>Total</Typography>
                            <Typography sx={{ fontWeight: 800, color: COLORS.primary, fontSize: '1.25rem' }}>
                                {formatUGX(totalUGX)}
                            </Typography>
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleProceed}
                            disabled={!selectedCategory}
                            sx={{
                                backgroundColor: COLORS.primary,
                                '&:hover': { backgroundColor: '#1e3329' },
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                py: 1.5,
                            }}
                        >
                            Proceed to Payment
                        </Button>
                    </CardContent>
                </Card>
            </Box>

            {/* Payment Dialog */}
            <MembershipPaymentDialog
                open={paymentDialogOpen}
                onClose={() => setPaymentDialogOpen(false)}
                formId={formData?.id}
                categoryId={selectedCategory?.id}
                categoryName={selectedCategory?.name}
                applicantName={applicantName}
                applicantEmail={applicantEmail}
                fieldData={fieldValues}
                voluntaryAmountUGX={voluntaryUGX}
                voluntaryAmountUSD={voluntaryUGX * UGX_TO_USD}
                totalAmountUGX={totalUGX}
                totalAmountUSD={totalUGX * UGX_TO_USD}
                formTitle={formData?.title}
                formType={formType}
            />
        </Box>
    );
};

export default MembershipFormInner;
